import React, { useEffect, useState, useCallback } from 'react';
import { ShieldCheck, AlertTriangle, ChevronRight, Vault } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const headers = () => {
  const t = localStorage.getItem('admin_token') || localStorage.getItem('auth_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * Continuity System operational mini-strip on the admin home.
 *
 * Always rendered — three visual states:
 *   • urgent    — pending approvals OR DMS stage 3+ (rose)
 *   • watch     — stale summaries only (amber)
 *   • clear     — vaults exist, nothing to action (emerald, calm)
 *   • empty     — no vaults activated yet (slate, neutral note)
 *
 * The "clear" and "empty" states are intentionally shown so the operator
 * always has a confirmed signal on the admin home — never a missing widget
 * that could be confused with "is it broken?".
 */
const EstateVaultAdminStrip = ({ onGoToVault }) => {
  const [counts, setCounts] = useState({
    pendingApprovals: 0,
    staleSummaries: 0,
    dmsAdvanced: 0,
    totalVaults: 0,
    activeVaults: 0,
  });
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const [fr, verifs, vaults] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/estate-vault/force-release/pending`, { headers: headers() }),
        fetch(`${BACKEND_URL}/api/admin/estate-vault/verifications/pending`, { headers: headers() }),
        fetch(`${BACKEND_URL}/api/admin/estate-vaults`, { headers: headers() }),
      ]);
      if (!fr.ok || !verifs.ok || !vaults.ok) return;
      const frJ = await fr.json();
      const vJ = await verifs.json();
      const vaJ = await vaults.json();
      const allRows = vaJ.rows || [];
      const activeRows = allRows.filter((v) => (v.status || '').toLowerCase() === 'active');
      const now = Date.now();
      const STALE_DAYS = 30;
      const stale = activeRows.filter((v) => {
        const t = v.last_summary_at || v.activated_at;
        if (!t) return true;
        return (now - new Date(t).getTime()) / (1000 * 60 * 60 * 24) >= STALE_DAYS;
      }).length;
      const dms = activeRows.filter((v) => (v.dms_stage || 0) >= 3).length;
      setCounts({
        pendingApprovals: (frJ.rows || []).length + (vJ.rows || []).length,
        staleSummaries: stale,
        dmsAdvanced: dms,
        totalVaults: allRows.length,
        activeVaults: activeRows.length,
      });
    } catch (_) { /* leave the empty state visible */ }
    finally { setLoaded(true); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Don't flash an empty state before the first request completes.
  if (!loaded) return null;

  const { pendingApprovals, staleSummaries, dmsAdvanced, totalVaults, activeVaults } = counts;
  const total = pendingApprovals + staleSummaries + dmsAdvanced;
  const urgent = pendingApprovals > 0 || dmsAdvanced > 0;
  const watch = !urgent && staleSummaries > 0;
  const hasNoVaults = totalVaults === 0;

  // ---------- EMPTY: no vaults activated yet ----------
  if (hasNoVaults) {
    return (
      <button
        type="button"
        onClick={onGoToVault}
        className="w-full flex items-center justify-between gap-3 border rounded-lg px-3 py-2 mb-4 transition-colors text-left border-gray-700 bg-gray-900/40 hover:bg-gray-800/60"
        data-testid="ev-admin-home-strip"
        data-state="empty"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Vault className="w-4 h-4 text-gray-400 shrink-0" />
          <span className="text-sm text-gray-200 truncate">
            <span className="font-medium text-white">Continuity System:</span>{' '}
            <span className="text-gray-400">No portfolio continuity has been activated yet — nothing to approve.</span>
          </span>
        </div>
        <span className="text-xs text-gray-400 shrink-0 flex items-center gap-1">
          Open <ChevronRight className="w-3.5 h-3.5" />
        </span>
      </button>
    );
  }

  // ---------- CLEAR: vaults exist, nothing pending ----------
  if (total === 0) {
    return (
      <button
        type="button"
        onClick={onGoToVault}
        className="w-full flex items-center justify-between gap-3 border rounded-lg px-3 py-2 mb-4 transition-colors text-left border-emerald-500/40 bg-emerald-950/20 hover:bg-emerald-950/40"
        data-testid="ev-admin-home-strip"
        data-state="clear"
      >
        <div className="flex items-center gap-2 min-w-0">
          <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
          <span className="text-sm text-gray-200 truncate">
            <span className="font-medium text-white">Continuity System:</span>{' '}
            <span className="text-emerald-300">All clear</span>
            <span className="text-gray-400">
              {' '}&middot; {activeVaults} active vault{activeVaults === 1 ? '' : 's'}, no approvals pending.
            </span>
          </span>
        </div>
        <span className="text-xs text-gray-300 shrink-0 flex items-center gap-1">
          Open <ChevronRight className="w-3.5 h-3.5" />
        </span>
      </button>
    );
  }

  // ---------- BUSY: pending approvals / stale / DMS stage 3+ ----------
  return (
    <button
      type="button"
      onClick={onGoToVault}
      className={`w-full flex items-center justify-between gap-3 border rounded-lg px-3 py-2 mb-4 transition-colors text-left ${
        urgent ? 'border-rose-500/60 bg-rose-950/30 hover:bg-rose-950/50' : 'border-amber-500/40 bg-amber-950/20 hover:bg-amber-950/40'
      }`}
      data-testid="ev-admin-home-strip"
      data-state={urgent ? 'urgent' : 'watch'}
    >
      <div className="flex items-center gap-2 min-w-0">
        {urgent ? <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0" /> : <ShieldCheck className="w-4 h-4 text-amber-400 shrink-0" />}
        <span className="text-sm font-medium text-white truncate">
          Continuity System:{' '}
          {pendingApprovals > 0 && (
            <span className="text-rose-300">{pendingApprovals} pending approval{pendingApprovals === 1 ? '' : 's'}</span>
          )}
          {pendingApprovals > 0 && (staleSummaries > 0 || dmsAdvanced > 0) && <span className="text-gray-500"> &middot; </span>}
          {staleSummaries > 0 && (
            <span className="text-amber-300">{staleSummaries} stale summar{staleSummaries === 1 ? 'y' : 'ies'}</span>
          )}
          {staleSummaries > 0 && dmsAdvanced > 0 && <span className="text-gray-500"> &middot; </span>}
          {dmsAdvanced > 0 && (
            <span className="text-orange-300">{dmsAdvanced} vault{dmsAdvanced === 1 ? '' : 's'} at DMS stage 3+</span>
          )}
        </span>
      </div>
      <span className="text-xs text-gray-300 shrink-0 flex items-center gap-1">Open <ChevronRight className="w-3.5 h-3.5" /></span>
    </button>
  );
};

export default EstateVaultAdminStrip;
